import { getSupabaseClient } from '../../../../core/supabase/client.js';
import type { NotificationDto } from '../dtos/NotificationDto.js';

const SELECT_FIELDS =
  'id, org_id, project_id, user_id, title, body, type, resource, resource_id, metadata, is_read, created_at';

export interface NotificationListOptions {
  projectId?: string;
  unreadOnly?: boolean;
  type?: string;
  limit?: number;
  offset?: number;
}

export class NotificationRemoteDataSource {
  private get supabase() {
    return getSupabaseClient() as any;
  }

  /** Plain list with filters. RLS scopes rows to auth.uid(). Unread first, then newest. */
  async list(options: NotificationListOptions): Promise<NotificationDto[]> {
    const { projectId, unreadOnly, type, limit = 20, offset = 0 } = options;
    let query = this.supabase
      .from('notifies')
      .select(SELECT_FIELDS)
      .order('is_read', { ascending: true })
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (projectId) query = query.eq('project_id', projectId);
    if (unreadOnly) query = query.eq('is_read', false);
    if (type) query = query.eq('type', type);

    const { data, error } = await query;
    if (error) throw new Error(error.message);
    return (data ?? []) as NotificationDto[];
  }

  /** Full-text search via the pgroonga-backed RPC (note: not project-scoped server-side). */
  async search(options: { query: string; limit?: number; offset?: number }): Promise<NotificationDto[]> {
    const { query, limit = 20, offset = 0 } = options;
    const { data, error } = await this.supabase.rpc('search_notifies', {
      search_query: query,
      row_limit: limit,
      row_offset: offset,
    });
    if (error) throw new Error(error.message);
    return (data ?? []) as NotificationDto[];
  }

  async unreadCount(projectId?: string): Promise<number> {
    let query = this.supabase
      .from('notifies')
      .select('id', { count: 'exact', head: true })
      .eq('is_read', false);
    if (projectId) query = query.eq('project_id', projectId);

    const { count, error } = await query;
    if (error) throw new Error(error.message);
    return count ?? 0;
  }

  async markRead(id: string): Promise<void> {
    const { error } = await this.supabase.from('notifies').update({ is_read: true }).eq('id', id);
    if (error) throw new Error(error.message);
  }

  async markAllRead(projectId?: string): Promise<void> {
    let query = this.supabase.from('notifies').update({ is_read: true }).eq('is_read', false);
    if (projectId) query = query.eq('project_id', projectId);
    const { error } = await query;
    if (error) throw new Error(error.message);
  }

  async deleteRead(): Promise<void> {
    const { error } = await this.supabase.from('notifies').delete().eq('is_read', true);
    if (error) throw new Error(error.message);
  }

  subscribe(userId: string, onChange: () => void): () => void {
    const supa = this.supabase;
    const channel = supa.channel(`notifies_${userId}`);
    channel.on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'notifies', filter: `user_id=eq.${userId}` },
      () => onChange()
    );
    channel.subscribe();
    return () => { supa.removeChannel(channel); };
  }
}
